import { CityStoreFacade } from '@lentau/city/store/city.facade';
import { computed, effect, inject, Injectable, Signal, untracked, WritableSignal } from '@angular/core';
import { PickupStore, UserAddress } from '@lentau/address/shared/interfaces';
import { DeliveryModeFacade } from '@lentau/delivery-mode/store/delivery-mode.facade';
import { AddressesFacade } from '@lentau/delivery-mode/store/addresses.facade';
import { toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, debounceTime, distinctUntilChanged, filter, map, Observable, skipWhile, take } from 'rxjs';
import { Region } from 'angular-api-gateway/v1/model/region';
import { Zone } from 'angular-api-gateway/v1/model/zone';
import { AddressOrUserChangeArgs, MissionAddressMode } from './types';
import { isValidArrayData } from '@lentau/ui-kit/core/utils/array.utils';
import {
  ANALYTIC_MISSION_TOKEN,
  ANALYTIC_HUB_NAME_EMPTY,
  ANALYTIC_HUB_NAME_TOKEN,
  ANALYTIC_MISSION_EMPTY,
} from '@lentau/analytics/events/shared/tokens/event-catalog-base-params.tokens';
import { DeliveryMode } from '@lentau/delivery-mode/shared/enums';
import { UserFacade } from '@lentau/profile/store/user/user.facade';
import { REQUEST_STATE } from '@lentau/ui-kit/core/enums/request-state.enum';
import { PlatformService } from '@lentau/ui-kit/core/services/platform/platform.service';
import { RECEIVING_METHOD_ZONE_TO_STORE_TYPE } from '@lentau/address/shared/constants';

@Injectable({
  providedIn: 'root',
})
export class MissionAddressService {
  private addressesFacade: AddressesFacade = inject(AddressesFacade);
  private deliveryModeFacade: DeliveryModeFacade = inject(DeliveryModeFacade);
  private cityFacade: CityStoreFacade = inject(CityStoreFacade);
  private missionToken: WritableSignal<string | null> = inject(ANALYTIC_MISSION_TOKEN);
  private hubNameToken: WritableSignal<string | null> = inject(ANALYTIC_HUB_NAME_TOKEN);
  private userFacade: UserFacade = inject(UserFacade);
  private platformService: PlatformService = inject(PlatformService);

  private zones: Signal<Zone[]> = this.deliveryModeFacade.zones;

  private selectedZone: Signal<Zone | undefined> = computed(() => {
    const zones: Zone[] = this.zones();
    if (!isValidArrayData(zones)) {
      return undefined;
    }

    return zones.find((zone: Zone) => zone.isSelected);
  });

  private selectedZoneType: Signal<string> = computed(() => {
    return this.selectedZone()?.type ?? ANALYTIC_MISSION_EMPTY;
  });

  private selectedZoneHubName: Signal<string> = computed(() => {
    return this.selectedZone()?.store?.storeAlias ?? ANALYTIC_HUB_NAME_EMPTY;
  });

  public isDelivery: Signal<boolean> = this.deliveryModeFacade.isDelivery;

  public isShop: Signal<boolean> = this.deliveryModeFacade.isShop;

  public currentStore: Signal<PickupStore> = this.deliveryModeFacade.currentStore;
  public deliveryAddress: Signal<UserAddress> = this.addressesFacade.currentAddress;
  public selectedDeliveryStore: Signal<PickupStore> = computed(() => {
    if (!this.isDelivery()) return undefined;

    const storeId: number = this.selectedZone()?.store.storeId;
    return this.deliveryModeFacade.pickupList().find((item: PickupStore) => item.id === storeId);
  });

  // Выбранный магазин, если выбрана доставка, то магазин из которого доставят
  public currentSelectedStore: Signal<PickupStore> = computed(() =>
    this.isDelivery() ? this.selectedDeliveryStore() : this.currentStore()
  );

  public isNotDefault: Signal<boolean> = this.deliveryModeFacade.isNotDefault;

  public missionAddressGet: Signal<PickupStore | UserAddress> = computed(() =>
    this.isDelivery() ? this.deliveryAddress() : this.currentStore()
  );

  // Выбранный адрес доставки/самовывоза
  public missionAddress: Signal<PickupStore | UserAddress> = computed(() => this.missionAddressGet(), {
    equal: (prev: PickupStore | UserAddress, cur: PickupStore | UserAddress) => {
      if (!prev || !cur) return false;

      // сравнение для типа PickupStore
      if ('id' in cur && 'id' in prev) {
        return (
          cur.id === prev.id &&
          cur.pickupWindowHours?.open === prev.pickupWindowHours?.open &&
          cur.pickupWindowHours?.close === prev.pickupWindowHours?.close
        );
      }

      // сравнение для типа UserAddress
      if ('Id' in cur && 'Id' in prev) {
        // у разных адресов доставки ид будут совпадать для неавторизованного
        const isEqual: boolean =
          cur.Id === prev.Id &&
          cur.FlatNumber === prev.FlatNumber &&
          cur.ZoneType === prev.ZoneType &&
          cur.YandexLatitude === prev.YandexLatitude &&
          cur.YandexLongitude === prev.YandexLongitude &&
          !prev.fromCache;
        return isEqual;
      }

      // prev и cur разного типа
      return false;
    },
  });
  public missionAddress$: Observable<PickupStore | UserAddress> = toObservable(this.missionAddress);

  public hasMissionAddress: Signal<boolean> = computed(() => !!this.missionAddress());
  public hasMissionAddress$: Observable<boolean> = toObservable(this.hasMissionAddress);

  public hasDeliveryAddresses: Signal<boolean> = computed(() => isValidArrayData(this.addressesFacade.list()));
  public hasPickupAddresses: Signal<boolean> = computed(() =>
    isValidArrayData(this.deliveryModeFacade.previousPickups())
  );
  public hasOfflineAddresses: Signal<boolean> = computed(() =>
    isValidArrayData(this.deliveryModeFacade.previousOffline())
  );

  public missionAddressId: Signal<string | number> = computed(
    () => (this.missionAddress() as PickupStore)?.id || (this.missionAddress() as UserAddress)?.Id
  );
  public missionAddressId$: Observable<string | number> = toObservable(this.missionAddressId);

  public missionAddressZone: Signal<string> = computed(() => (this.missionAddress() as UserAddress)?.ZoneType);
  public missionAddressZone$: Observable<string> = toObservable(this.missionAddressZone);

  public deliveryMode: Signal<DeliveryMode> = this.deliveryModeFacade.type;

  public fullAddressMode: Signal<MissionAddressMode> = computed(() => ({
    type: this.deliveryMode(),
    missionAddress: this.missionAddress(),
    missionAddressId: this.missionAddressId(),
    isDefaultStore: !this.isNotDefault(),
    missionZone: this.missionAddressZone(),
  }));
  public fullAddressMode$: Observable<MissionAddressMode> = toObservable(this.fullAddressMode);

  // Заголовок выбранного адреса доставки/самовывоза
  public missionAddressTitle: Signal<string> = computed(() => {
    if (this.isDelivery()) {
      const address: UserAddress = this.missionAddress() as UserAddress;
      return address?.AddressFine || address?.TownTitle || '';
    } else {
      const store: PickupStore = this.missionAddress() as PickupStore;
      return store?.addressFull || '';
    }
  });

  public missionAddressCityName: Signal<string> = computed(() => {
    if (this.isDelivery()) {
      const address: UserAddress = this.missionAddress() as UserAddress;
      return address?.TownTitle || '';
    } else {
      const store: PickupStore = this.missionAddress() as PickupStore;
      return this.cityFacade.cities()?.find((region: Region) => region.id === store?.regionId)?.name || '';
    }
  });

  public isAddressDataLoading: Signal<boolean> = computed(
    () => this.addressesFacade.isLoading() || this.deliveryModeFacade.previousStoresIsLoading()
  );
  public isAddressDataLoading$: Observable<boolean> = toObservable(this.isAddressDataLoading);

  public addressOrUserChange$: Observable<AddressOrUserChangeArgs> = this.getAddressOrUserChange();

  public waitForUpdatingAddressAndMissionData$: Observable<unknown> = this.getWaitForUpdatingAddressAndMissionData();

  constructor() {
    // Аналитика на SSR не нужна — токены обновляем только в браузере
    if (this.platformService.isServer) {
      return;
    }

    this.initializeAnalyticsTokens();
  }

  private initializeAnalyticsTokens(): void {
    effect((): void => {
      const address: PickupStore | UserAddress = this.missionAddress();
      const isDelivery: boolean = this.isDelivery();
      const zoneType: string = this.selectedZoneType();
      const zoneHubName: string = this.selectedZoneHubName();

      untracked((): void => {
        if (isDelivery) {
          this.missionToken.set(RECEIVING_METHOD_ZONE_TO_STORE_TYPE[zoneType]);
          this.hubNameToken.set(zoneHubName);

          return;
        }

        this.missionToken.set((address as PickupStore)?.marketType ?? ANALYTIC_MISSION_EMPTY);
        this.hubNameToken.set((address as PickupStore)?.title ?? ANALYTIC_HUB_NAME_EMPTY);
      });
    });
  }

  private getAddressOrUserChange(): Observable<AddressOrUserChangeArgs> {
    const addressChangeEventsStream$: Observable<AddressOrUserChangeArgs> = combineLatest([
      this.missionAddressId$,
      this.missionAddressZone$,
      this.deliveryModeFacade.type$,
      this.userFacade.legalEntityId$.pipe(
        map((legalEntityId: string) => legalEntityId || ''),
        distinctUntilChanged()
      ),
    ]).pipe(
      debounceTime(0),
      distinctUntilChanged((prev: AddressOrUserChangeArgs, curr: AddressOrUserChangeArgs): boolean =>
        prev.every((val: string | number | DeliveryMode, index: number): boolean => val === curr[index])
      )
    );

    return addressChangeEventsStream$;
  }

  private getWaitForUpdatingAddressAndMissionData(): Observable<unknown> {
    return combineLatest([
      this.isAddressDataLoading$.pipe(
        skipWhile((isLoading: boolean): boolean => !isLoading),
        filter((isLoading: boolean): boolean => !isLoading),
        take(1)
      ),
      this.deliveryModeFacade.requestState$.pipe(
        skipWhile((state: REQUEST_STATE): boolean => state !== REQUEST_STATE.LOADING),
        filter((state: REQUEST_STATE): boolean => state !== REQUEST_STATE.LOADING),
        take(1)
      ),
    ]);
  }
}
